<template>
  <div>
    <div class="main" @click="selectedPlacementId = null">
      <zoom-view class="zoom-view" :zoom="zoom">
        <template v-slot:outer>
          <b-alert v-if="feedIn.tooHighElectricCurrent" show variant="danger" class="order-alert">
            {{ $t("tooHighElectricCurrent", { feedInCurrent: feedInCurrent }) }}</b-alert>
          <b-alert v-if="disabled" show variant="success" class="order-alert">
            {{ $t(`project.status.message.${$store.getters["projects/status"]}`) }}</b-alert>
        </template>
        <media-column-side :class="{ loading }" style="transition: opacity 0.4s" ref="columnSide"
          :columnType="columnType" :columnConfiguration="columnConfiguration" :modulePlacements="modulePlacements"
          :emptyModules="emptyModules" :modulePriceHover="modulePriceHover" :disabled="disabled" :dragItem="dragItem"
          :selectedPlacement="selectedPlacementId" @selectPlacement="selectPlacement"
          @dragStart="onDragStart($event.event, $event.module, $event.placement)" @dragEnd="onDragEnd"
          @setModuleHover="setModuleHover($event.event, $event.module)"
          @changeHeightOfMediacolumn="changeHeightOfMediacolumn($event)" />
      </zoom-view>

      <div v-if="selectedPlacement" class="module-tooltip" ref="moduleTooltip" @click.stop>
        {{ selectedPlacement.module.description }}
        <div style="font-size: 12px; opacity: 0.5;">{{ selectedPlacement.module.itemNumber }}</div>
        <b-button class="mt-2 p-1" size="sm" variant="icon" @click="deletePlacement(selectedPlacement.placement)">
          <b-icon icon="trash" class="mr-1" />
          {{ $t('configuration.delete.delete') }}
        </b-button>
      </div>

      <div class="zoom-controls">
        <button :disabled="zoom == 1" @click="setZoom(zoom / 1.1892072)">
          <b-icon icon="zoom-in" />
        </button>
        <button :disabled="zoom == 4" @click="setZoom(zoom * 1.1892072)">
          <b-icon icon="zoom-out" />
        </button>
        <b-button class="btn3D" @click="modal3dOpen = true" pill variant="outline-secondary">
          <svg viewBox="0 0 24 24" fill="currentColor" width="24">
            <path
              d="M7.53 21.48C4.26 19.94 1.92 16.76 1.56 13H.06c.51 6.16 5.66 11 11.95 11l.66-.03-3.81-3.81-1.33 1.32zm.89-6.52c-.19 0-.37-.03-.52-.08-.16-.06-.29-.13-.4-.24-.11-.1-.2-.22-.26-.37-.06-.14-.09-.3-.09-.47h-1.3c0 .36.07.68.21.95.14.27.33.5.56.69.24.18.51.32.82.41.3.1.62.15.96.15.37 0 .72-.05 1.03-.15.32-.1.6-.25.83-.44s.42-.43.55-.72.2-.61.2-.97c0-.19-.02-.38-.07-.56-.05-.18-.12-.35-.23-.51-.1-.16-.24-.3-.4-.43-.17-.13-.37-.23-.61-.31.2-.09.37-.2.52-.33.15-.13.27-.27.37-.42.1-.15.17-.3.22-.46s.07-.32.07-.48c0-.36-.06-.68-.18-.96s-.29-.51-.51-.69c-.2-.19-.47-.33-.77-.43C9.11 8.05 8.77 8 8.4 8c-.36 0-.69.05-1 .16-.3.11-.57.26-.79.45-.21.19-.38.41-.51.67-.12.26-.18.54-.18.85h1.3c0-.17.03-.32.09-.45s.14-.25.25-.34.23-.17.38-.22.3-.08.48-.08c.4 0 .7.1.89.31.19.2.29.49.29.86 0 .18-.03.34-.08.49s-.14.27-.25.37c-.11.1-.25.18-.41.24-.16.06-.36.09-.58.09h-.77v1.03h.77c.22 0 .42.02.6.07s.33.13.45.23c.12.11.22.24.29.4s.1.35.1.57c0 .41-.12.72-.35.93-.23.23-.55.33-.95.33zm8.55-5.92c-.32-.33-.7-.59-1.14-.77-.44-.18-.93-.27-1.47-.27H12v8h2.3c.55 0 1.06-.09 1.51-.27s.84-.43 1.16-.76c.32-.33.57-.73.74-1.19.17-.47.26-.99.26-1.57v-.4c0-.58-.09-1.1-.26-1.57s-.42-.87-.74-1.2zm-.39 3.16c0 .42-.05.79-.14 1.13-.1.33-.24.62-.43.85-.19.23-.43.41-.71.53-.29.12-.62.18-.99.18h-.91V9.12h.97c.72 0 1.27.23 1.64.69.38.46.57 1.12.57 1.99v.4zM12.01 0l-.66.03 3.81 3.81 1.33-1.33c3.27 1.55 5.61 4.72 5.96 8.48h1.5C23.45 4.84 18.3 0 12.01 0z" />
          </svg>
        </b-button>
        <template v-if="feedIn.recommendedModules.length > 0 || requiredFuseBoxModules.length > 0">
          <button v-b-tooltip.hover :title="$t('configuration.powerSupplyMissing')"
            v-if="feedIn.recommendedModules.length > 0" @click="openModulesModal()">
            <font-awesome-icon icon="exclamation-triangle" color="red" size="sm" />
          </button>
          <button v-b-tooltip.hover :title="$t('configuration.fuseBoxRequired')"
            v-if="requiredFuseBoxModules.length > 0" @click="openModulesModal()">
            <font-awesome-icon icon="exclamation-triangle" color="red" size="sm" />
          </button>
        </template>
      </div>

      <b-modal ref="modulesModal" v-if="columnType != null && !disabled" hide-header size="md" ok-only
        :ok-title="$t('close')" :lazy="true">
        <module-sidebar v-if="!electricProtectionView" :recommendedModules="feedIn.recommendedModules" :columnType="columnType"
          :disabled="disabled" @dragStart="onDragStart($event.event, $event.module)" @dragEnd="onDragEnd"
          @showFeedingDialog="showFeedingDialog()" @addModule="addModule" class="module-sidebar" />
        <ElectricalProtection :currentDragItem="null" v-else />
      </b-modal>

      <b-modal id="continue-delete-module" class="text-left" ref="changedialog"
        :title="$t('changeHeightOfColumn.removeModule')">
        <span>{{ $t("changeHeightOfColumn.removeModuleToShorten") }}</span>
        <template slot="modal-footer">
          <b-button @click="$refs.changedialog.hide()">{{
          $t("cancel")
          }}</b-button>
          <b-button variant="danger" @click="
            (columnConfiguration.height -= 125),
              saveChanges(),
              $refs.changedialog.hide()
          ">{{ $t("changeHeightOfColumn.shorten") }}</b-button>
        </template>
      </b-modal>
      <continue-with-problems-dialog ref="continueWithProblemsDialog" :warning="feedIn.warning"
        :tooHighElectricCurrent="feedIn.tooHighElectricCurrent" :feedInCurrent="feedInCurrent" />
    </div>
    <div class="trashbin" :class="{ big: dragItem, hover: trashed }"
      @dragenter="$event.preventDefault(), (trashed = true)" @drop="onDragEnd">
      <b-icon scale="1.4" icon="trash" />
    </div>
    <div class="add-module">
      <b-button @click="openModulesModal()" class="btn-primary" variant="primary">
        <div class="d-flex">
          <div class="d-flex align-items-center">
            <b-icon scale="1.8" icon="plus" />
          </div>
          <div class="pl-2 d-none d-md-block pr-1" v-html="$t('configuration.addModule')"></div>
        </div>
      </b-button>
    </div>
    <ContactPopover />

    <Modal3d :open.sync="modal3dOpen" />
  </div>
</template>

<script>
import ModuleSidebar from "./ModulePlacements/ModuleSidebar";
import ContinueWithProblemsDialog from "../components/ContinueWithProblemsDialog.vue";
import MediaColumnSide from "./ModulePlacements/MediaColumnSide";
import ZoomView from "../components/ZoomView";
import ElectricalProtection from "./ModulePlacements/ElectricalProtection";
import ContactPopover from "../components/ContactPopover";
import Modal3d from "../components/Modal3d";
import { jibCraneSpan } from "../jibCrane";
import { createPopper } from "@popperjs/core"

import breakerMixin from "./ModulePlacements/breakerMixin.js";

export default {
  mixins: [breakerMixin],
  components: {
    Modal3d,
    ContinueWithProblemsDialog,
    ModuleSidebar,
    MediaColumnSide,
    ZoomView,
    ElectricalProtection,
    ContactPopover
  },
  data() {
    return {
      electricProtectionView: false,
      showContact: false,
      moduleHover: null,
      modulePriceHover: null,

      helpNeeded: false,
      zoom: 1,
      dragItem: null,
      trashed: false,
      loading: false,
      modal3dOpen: false,
      selectedPlacementId: null
    };
  },
  computed: {
    selectedPlacement() {
      const placement = this.modulePlacements.find(p => p.id != null && p.id == this.selectedPlacementId)
      if (!placement) return null
      return {
        placement: { ...placement.position, placement },
        module: this.$store.state.modules.find(m => m.itemNumber == placement.itemNumber),
      }
    },
    jibCraneSpan() {
      return jibCraneSpan();
    },
    columnConfiguration() {
      return this.$store.state.configurations.configuration;
    },
    disabled() {
      return this.$store.getters["projects/disabled"];
    },
    modulePlacements() {
      if (
        this.$store.state.configurations.configuration == null ||
        this.$store.state.configurations.emptyModulePlacements == null
      ) return []
      return [
        ...this.$store.state.configurations.configuration.modulePlacements,
        ...this.$store.state.configurations.emptyModulePlacements.map(m => {
          return {
            automatic: true,
            itemNumber: m.module.itemNumber,
            position: {
              type: "grid",
              side: m.side,
              gridIndex: m.gridIndex
            }
          };
        })
      ].map(p => ({
        ...p,
        id: Math.random()
      }))
    },
    columnType() {
      return this.$store.state.columnTypes.find(
        columnType => {
          return columnType.id == this.columnConfiguration.columnType;
        }
      );
    },
    modules() {
      return this.$store.state.modules
    },
    emptyModules() {
      return this.$store.state.configurations.emptyModulePlacements
    },
    feedInCurrent() {
      if (!this.columnConfiguration) return null;
      return this.columnConfiguration.electricProtection
        ? this.columnConfiguration.electricProtection.feedInCurrent
        : null;
    },
    feedIn() {
      return this.$store.state.configurations.feedingValidation || {
        warning: [],
        tooHighElectricCurrent: false,
        recommendedModules: []
      }
    }
  },
  created() {
    this.setZoom(
      (1 / window.innerHeight) *
      ((this.columnConfiguration.height / 125) * 125 + 200)
    )
  },
  methods: {
    selectPlacement(placementId, referenceEl) {
      this.selectedPlacementId = placementId
      this.$nextTick(() => {
        createPopper(referenceEl, this.$refs.moduleTooltip, {
          placement: "right",
          modifiers: [
            {
              name: "flip",
              enabled: true
            },
            {
              name: "offset",
              options: { offset: [0, 12] }
            }
          ]
        })
      })
    },
    openModulesModal() {
      this.$refs.modulesModal.show();
    },
    async addModule(module) {
      this.$refs.modulesModal.hide();

      await this.$store.dispatch("configurations/loadInvalidPlacements", {
        itemNumber: module.itemNumber
      });

      const forbiddenToDrop = this.$store.state.configurations.forbiddenToDrop;
      for (const side of this.columnType.arableSites) {
        for (let i = this.columnConfiguration.height / 125; i--;) {
          if (
            forbiddenToDrop.some(
              p =>
                p.placement.position.gridIndex == i &&
                p.placement.position.side == side
            )
          )
            continue;

          this.$store.commit("configurations/addModulePlacement", {
            position: {
              gridIndex: i,
              side
            },
            itemNumber: module.itemNumber
          });

          return;
        }
      }
    },
    onDragStart(event, module, placement) {
      this.$refs.modulesModal.hide();
      this.selectedPlacementId = null
      this.dragItem = { module, placement, offset: event.currentTarget.getBoundingClientRect().bottom - event.clientY };
      this.$store.commit("configurations/setForbiddenToDrop", null);
      this.$store.dispatch("configurations/loadInvalidPlacements", {
        itemNumber: module.itemNumber,
        ignore: placement
          ? this.modulePlacements.findIndex(mp => {
            return (
              mp.position.gridIndex == placement.gridIndex &&
              mp.position.side == placement.side
            );
          })
          : undefined
      });
    },
    async onDragEnd() {
      if (this.trashed && this.dragItem.placement) {
        this.deletePlacement(this.dragItem.placement)
      }
      this.dragItem = null;
      this.trashed = false;
    },
    deletePlacement(placement) {
      this.$store.commit(
        "configurations/deleteModulePlacement",
        placement
      );
      this.selectedPlacementId = null
    },
    setZoom(zoom) {
      this.zoom = Math.max(Math.min(zoom, 4), 1);
    },
    setModuleHover(event, module) {
      this.moduleHover = module == 0 ? null : module;
    },
    setModulePriceHover(event, itemNumber) {
      this.modulePriceHover = itemNumber;
    },

    async changeHeightOfMediacolumn(operator) {
      if (this.disabled) return false;
      if (operator == "minus") {
        if (
          this.columnConfiguration.height / 125 > 4 &&
          (!this.columnConfiguration.jibCrane ||
            this.columnConfiguration.height - 125 >=
            this.columnConfiguration.jibCrane.height + this.jibCraneSpan)
        ) {
          if (
            this.$store.state.configurations.configuration.modulePlacements.find(
              p =>
                p.position.gridIndex ==
                this.columnConfiguration.height / 125 -
                this.moduleForSlot(p.position.side, p.position.gridIndex)
                  .slotHeight
            )
          ) {
            this.$refs.changedialog.show();
          } else {
            this.columnConfiguration.height -= 125;
            if (this.columnConfiguration.jibCrane) {
              this.columnConfiguration.jibCrane.height = Math.min(
                this.columnConfiguration.jibCrane.height,
                this.columnConfiguration.height - 538
              );
            }
            await this.saveChanges();
          }
        }
      } else {

        const maxHeight = this.$store.state.columnTypes.find(
          type => type.id == this.columnConfiguration.columnType
        ).maxHeight;

        if (this.columnConfiguration.height < maxHeight) {
          this.columnConfiguration.height += 125;
          await this.saveChanges();
        }
      }
    },
    async saveChanges() {
      const calculatedNewModuleSlots = this.$store.state.configurations.configuration.modulePlacements
        .filter(p => p.position.gridIndex + this.moduleForSlot(mp.position.side, mp.position.gridIndex).slotHeight
          <= this.columnConfiguration.height / 125);

      this.$store.commit("configurations/setConfiguration", {
        configuration: {
          ...this.columnConfiguration,
          height: this.columnConfiguration.height,
          jibCrane: this.columnConfiguration.jibCrane
            ? {
              ...this.columnConfiguration.jibCrane,
              height: Math.min(
                this.columnConfiguration.jibCrane.height,
                this.columnConfiguration.height
              )
            }
            : null,
          modulePlacements: calculatedNewModuleSlots
        }
      });
    },
    projectSubmitOrCancel() {
      this.$router.replace(
        this.$route.fullPath
          .toString()
          .substring(0, this.$route.fullPath.toString().indexOf("#edit"))
      );
    },
    showFeedingDialog() {
      if (
        !this.feedIn.tooHighElectricCurrent &&
        (this.feedIn.warning.length == 0 ||
          (this.feedIn.warning.length == 1 && this.feedIn.warning[0] == "Daten"))
      ) {
        this.$router.push({
          name: "shoppingCart",
          params: { projectId: this.$store.state.projects.project.number }
        });
      } else {
        this.$refs.continueWithProblemsDialog.show();
      }
    },
    moduleForSlot(side, gridIndex) {
      const placement = this.modulePlacements.find(
        p => p.position.side == side && p.position.gridIndex == gridIndex
      );
      if (placement == null) return null;
      const module = this.$store.state.modules.find(
        m => m.itemNumber == placement.itemNumber
      );
      return module == null ? null : module;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../styles/common";

.order-alert {
  text-align: center;
  font-size: initial;
  color: black;
}

.price-sidebar:not(.opened) {
  display: none;
}

::v-deep #contact-popover-container .help {
  margin-top: 90px;
}

.zoom-view {
  // position: absolute !important;
  width: 100%;
  height: 100%;
}

.zoom-controls {
  position: fixed;

  z-index: 5;
  margin-top: 20px;
}

.zoom-controls::before {
  content: "";
  display: block;
  position: absolute;
  z-index: -1;
  width: 100%;
  height: 100%;
  background: white;
  filter: blur(12px);
}

.zoom-controls button {
  display: block;
  cursor: pointer;
  padding: 5px;
  font-size: 24px;
  line-height: 1;
  border: none;
  outline: 0;
  color: rgb(58, 58, 58);
  background: none;
}

.zoom-controls button:disabled {
  cursor: inherit;
  opacity: 0.4;
}

.zoom-controls ._3d {
  margin-top: 8px;
}

.a-button {
  position: absolute;
  top: 8px;
  left: 8px;
}

.right-column:not(.module-sidebar-opened) {
  width: 30px;

  .module-sidebar {
    display: none;
  }
}

.add-module {
  position: fixed;
  right: 30px;
  margin-top: 20px;

  & .btn {
    border-radius: 10px;
    min-height: 40px;
  }
}

.btn3D {
  padding: 0;
  // position: fixed;
  // right: 30px;
  width: 40px;
  height: 40px;
  // margin-top: 90px;
}

.trashbin {
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: 70px;
  z-index: 10;
  width: 40px;
  height: 40px;
  right: 30px;
  position: fixed;
  background-color: rgb(226, 226, 226);
  // box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  transition: all 300ms;
  pointer-events: none;
  opacity: 1;
}

.trashbin.big {
  background-color: #e88;
  transform: scale(1.1);
  opacity: 1;
  pointer-events: all;
  width: 70px;
  height: 70px;
}

.trashbin.hover {
  background-color: #d44;
}

.toggle-button {
  position: absolute;
  bottom: 0;
}

.loading {
  opacity: 0.6;
  pointer-events: none;
}

.module-tooltip {
  position: absolute;
  z-index: 1;
  padding: 8px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 4px 12px #0002;
  border: 1px solid #0004;
}
</style>
